import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { cn } from '../../lib/cn.js'
import { money } from '../../lib/format.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon } from '../Icon.js'
import { DialogFooter, DialogHeader, Modal } from '../Modal.js'
import { Button, ErrorState, SkeletonRows, toneStyles } from '../ui.js'
import { serviceIcon, serviceTone } from './services.js'

/**
 * Adding services to a job.
 *
 * The catalogue is data, not a menu in this file, so the office adding "Bird
 * proofing" in Settings puts it here without a deployment. Several at once,
 * because the office books the inspection, the cockroach treatment and the rat
 * stations in one phone call and adding them one at a time is three round trips
 * to record one conversation.
 *
 * A service already on the job is shown greyed rather than hidden. "Where has
 * the termite inspection gone" is a worse question than "why can I not pick it
 * twice", and the answer to the second one is on the row.
 */
export function AddServiceSheet({
  open,
  jobId,
  already,
  onClose,
}: {
  open: boolean
  jobId: string
  /** `ServiceType` ids already on this job. */
  already: string[]
  onClose: () => void
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      labelledBy="add-service-title"
      className="max-w-[520px]"
    >
      {open ? <Body jobId={jobId} already={already} onClose={onClose} /> : null}
    </Modal>
  )
}

function Body({
  jobId,
  already,
  onClose,
}: {
  jobId: string
  already: string[]
  onClose: () => void
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const [picked, setPicked] = useState<string[]>([])

  const { data: catalogue, isPending, error } = useQuery(trpc.forms.serviceTypes.queryOptions())

  const add = useMutation(
    trpc.jobs.addServices.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries(trpc.jobs.pathFilter())
        onClose()
      },
    }),
  )

  const onJob = new Set(already)

  const toggle = (id: string) =>
    setPicked((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    )

  return (
    <>
      <DialogHeader id="add-service-title" title="Add a service" onClose={onClose} />

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        {isPending ? (
          <SkeletonRows />
        ) : error ? (
          <ErrorState message={errorMessage(error)} />
        ) : catalogue.length === 0 ? (
          <p className="text-[13px] text-ink-500">
            Nothing in the service catalogue yet. It is set up in Settings, and each service names
            the report it produces.
          </p>
        ) : (
          <ul className="grid gap-2 sm:grid-cols-2">
            {catalogue.map((type) => {
              const isOn = onJob.has(type.id)
              const selected = picked.includes(type.id)
              const tone = serviceTone(type.tone)

              return (
                <li key={type.id}>
                  <button
                    type="button"
                    role="checkbox"
                    aria-checked={selected}
                    disabled={isOn}
                    onClick={() => toggle(type.id)}
                    className={cn(
                      'flex w-full items-start gap-2.5 rounded-lg p-3 text-left ring-[1.5px] ring-inset transition-colors',
                      isOn
                        ? 'cursor-not-allowed bg-ink-50 text-ink-400 ring-ink-200'
                        : selected
                          ? toneStyles[tone]
                          : 'bg-white text-ink-800 ring-ink-200 hover:bg-ink-50',
                    )}
                  >
                    <Icon
                      name={serviceIcon(type.icon)}
                      className="mt-px size-[18px] shrink-0 opacity-80"
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="text-[12.5px] font-semibold">{type.name}</span>
                      <span className="text-[11.5px] opacity-75">
                        {isOn ? 'Already on this job' : catalogueLine(type)}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      className={cn(
                        'mt-px grid size-4 shrink-0 place-items-center rounded-[4px] ring-[1.5px] ring-inset',
                        selected ? 'bg-current/10 ring-current' : 'ring-ink-300',
                      )}
                    >
                      {selected ? <Icon name="check" className="size-2.5" strokeWidth={3} /> : null}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}

        {add.error ? (
          <div className="pt-3">
            <ErrorState message={errorMessage(add.error)} />
          </div>
        ) : null}
      </div>

      <DialogFooter
        hint={
          picked.length === 0
            ? 'Nothing picked'
            : `${picked.length} ${picked.length === 1 ? 'service' : 'services'} picked`
        }
      >
        <Button size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="sm"
          variant="primary"
          disabled={picked.length === 0}
          loading={add.isPending}
          onClick={() => add.mutate({ jobId, serviceTypeIds: picked })}
        >
          {picked.length > 1 ? `Add ${picked.length} services` : 'Add the service'}
        </Button>
      </DialogFooter>
    </>
  )
}

/**
 * What a catalogue row says under its name.
 *
 * The report is the most useful fact — it is what distinguishes a termite
 * inspection from a termite treatment — then how long it takes, then what it
 * usually costs. A service with none of the three says so rather than showing
 * a blank line.
 */
function catalogueLine(type: {
  form: { name: string } | null
  estimatedMinutes: number | null
  defaultPriceIncTax: number | null
}): string {
  const parts = [
    type.form?.name ?? 'No report',
    type.estimatedMinutes === null ? null : `${type.estimatedMinutes} min`,
    type.defaultPriceIncTax === null ? null : money(type.defaultPriceIncTax),
  ].filter(Boolean)

  return parts.join(' · ')
}
